// Compute Plucker rays (1,6,1,H,W) for a pinhole camera. Mirrors the
// Python implementation in ae/view_encoding.py — each pixel gets a ray
// (origin x direction, direction). The output is what Plucker2Code in
// ApprDecoder expects; the encoder internally bilinear-resizes whatever
// we pass to 512² → 256² → 128² before its convs, so this shape only
// has to match the ONNX input signature (H == mesh.manifest.uv_res).

import * as THREE from "three";

// The first 3 plücker channels are the ray MOMENT (origin × direction), which
// encodes absolute camera position/scale. The appr decoder can overfit to the
// capture-rig moment distribution, so off-rig viewing cameras extrapolate to
// dark "black spot" colors. momentScale scales those channels: 1 = full plücker,
// 0 = direction-only — the DEFAULT, since a bundle that needs the moment channels
// can say so and one that silently omits the field can't be assumed to want the
// overfit. Runtime A/B knob via ?moment= in OGSModel.jsx; consumed by both this
// CPU path and GpuPlucker's WGSL kernel.
export const pluckerConfig = { momentScale: 0 };

/** Shared default so the no-offset call allocates nothing per frame. */
export const ORIGIN_ZERO: readonly number[] = Object.freeze([0, 0, 0]);

/**
 * Move a camera world position into the splat's local frame, IN PLACE.
 *
 * Only the OFFSET half lives here, deliberately: the CPU path then multiplies by
 * originScale itself while the GPU path passes originScale into the shader as a
 * uniform (which is what keeps wgsl/plucker.wgsl byte-identical to its reference).
 * The subtraction is the part that has to agree, so it has exactly one definition
 * and both paths call it — parity by construction rather than by inspection.
 *
 * Order matters: subtract THEN scale. (world - offset) * scale is not
 * world * scale - offset; on a cm bundle those differ by a factor of 100.
 */
export function toLocalOrigin<T extends { x: number; y: number; z: number }>(
  origin: T,
  worldOffset: readonly number[] = ORIGIN_ZERO,
): T {
  origin.x -= worldOffset[0] ?? 0;
  origin.y -= worldOffset[1] ?? 0;
  origin.z -= worldOffset[2] ?? 0;
  return origin;
}

// Reused scratch for the world_rotation inverse — this runs once per branch per frame
// and CLAUDE.md forbids per-frame allocation on the inference path.
const ROTATION_INVERSE = new THREE.Quaternion();

/**
 * Undo the splat's world_rotation on a camera pose, IN PLACE — the ORIENTATION half of
 * the trip toLocalOrigin makes, and both halves live in one call so neither path can
 * apply one without the other (render/worldRotation.ts says why that would be silent).
 *
 * Order: AFTER toLocalOrigin, since the rotation is about the splat's own origin.
 * Uniform originScale commutes with it, so either side of the scale is the same number.
 * `worldRotation` null (every bundle that declares none) leaves the pose untouched.
 */
export function toLocalRotation(
  origin: THREE.Vector3,
  quaternion: THREE.Quaternion,
  worldRotation: THREE.Quaternion | null = null,
): void {
  if (!worldRotation) return;
  ROTATION_INVERSE.copy(worldRotation).invert();
  origin.applyQuaternion(ROTATION_INVERSE);
  quaternion.premultiply(ROTATION_INVERSE);
}

// Resolve + install the effective momentScale for a pipeline boot: the model/
// bundle default, overridden by the ?moment=<v> A/B knob. Shared by the
// single-region (ModelConfig.momentScale) and head+teeth (scene.json
// moment_scale) paths so the black-lines fix can't silently miss one of them.
export function applyMomentScale(defaultScale: number, search = window.location.search): number {
  pluckerConfig.momentScale = defaultScale;
  const raw = new URLSearchParams(search).get("moment");
  if (raw !== null && Number.isFinite(parseFloat(raw))) {
    pluckerConfig.momentScale = parseFloat(raw);
    console.info(`[plucker] momentScale=${pluckerConfig.momentScale} (?moment= override)`);
  }
  return pluckerConfig.momentScale;
}

// originScale + worldOffset express the camera position in the splat's LOCAL
// frame — the frame appr was trained in.
//
// The splat is placed at  world = local * worldScale + worldOffset  (see
// frameCameraToSplats in OGSModel.jsx), so the inverse a camera has to travel is
//
//     local = (world - worldOffset) * originScale,     originScale = 1/worldScale
//
// BOTH terms matter and only for the MOMENT: dir is invariant to translation and
// to scale, so it is untouched, but the moment (origin × dir) is neither.
// Omitting the offset conditions appr on a camera position it was never trained
// on. That stayed invisible for as long as every bundle shipping a world_offset
// also shipped moment_scale 0 (direction-only — translation-invariant, so the
// bug could not express itself); eyeline V10-C is the first to ship
// world_offset [0,1.55,0] WITH moment_scale 1.0, which is what surfaced it.
//
// Defaults (scale 1, offset 0) are byte-identical to the untransformed path.
export function computePluckerRays(
  camera: THREE.PerspectiveCamera,
  width: number,
  height: number,
  originScale = 1,
  worldOffset: readonly number[] = ORIGIN_ZERO,
  worldRotation: THREE.Quaternion | null = null,
): Float32Array {
  const out = new Float32Array(6 * width * height);
  const origin = toLocalOrigin(camera.getWorldPosition(new THREE.Vector3()), worldOffset);
  // Copied, never the camera's own: `camera` is the reused LocalFrameCamera stand-in.
  const orientation = new THREE.Quaternion().copy(camera.quaternion);
  toLocalRotation(origin, orientation, worldRotation);
  if (originScale !== 1) origin.multiplyScalar(originScale);
  const fovRad = (camera.fov * Math.PI) / 180;
  const tanFov = Math.tan(fovRad / 2);
  const aspect = camera.aspect;

  // Camera basis vectors in the splat's local frame
  const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(orientation);
  const right = new THREE.Vector3(1, 0, 0).applyQuaternion(orientation);
  const up = new THREE.Vector3(0, 1, 0).applyQuaternion(orientation);

  const dir = new THREE.Vector3();
  const moment = new THREE.Vector3();
  const stride = width * height;
  for (let y = 0; y < height; y++) {
    const ny = 1 - (2 * (y + 0.5)) / height; // flip Y so top = +1
    for (let x = 0; x < width; x++) {
      const nx = (2 * (x + 0.5)) / width - 1;
      dir.copy(forward)
        .addScaledVector(right, nx * tanFov * aspect)
        .addScaledVector(up, ny * tanFov)
        .normalize();
      moment.crossVectors(origin, dir);

      const ms = pluckerConfig.momentScale;
      const idx = y * width + x;
      out[0 * stride + idx] = moment.x * ms;
      out[1 * stride + idx] = moment.y * ms;
      out[2 * stride + idx] = moment.z * ms;
      out[3 * stride + idx] = dir.x;
      out[4 * stride + idx] = dir.y;
      out[5 * stride + idx] = dir.z;
    }
  }
  return out;
}
